import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CrudService } from './crud.service';
import { Company, AccountStatus, CompanyAdminCounts, CompanyBillingLevel, GeoAddress } from '../models/company';
import { PlanChangeResult } from '../models/billing-level';

@Injectable({
  providedIn: 'root',
})
export class SuperAdminCompanyService extends CrudService<Company> {
  constructor() {
    const http = inject(HttpClient);

    super(http, 'admin/companies');
  }

  // CRAT-1355 — counts for the level tabs / status filters on the Companies table.
  async getCounts(search?: string): Promise<CompanyAdminCounts> {
    return await firstValueFrom(
      this.http.get<CompanyAdminCounts>('admin/companies/counts', { params: search ? { search } : {} })
    );
  }

  // CRAT-1353 — Stripe owns the plan level; the Edit-tier dialog only offers tiers inside it.
  async getBillingLevel(companyId: string): Promise<CompanyBillingLevel> {
    return await firstValueFrom(this.http.get<CompanyBillingLevel>(`admin/companies/${companyId}/billing-level`));
  }

  // CRAT-1359 — change the plan level / package in Stripe; the tier follows via webhook.
  async changePlan(companyId: string, priceId: string): Promise<PlanChangeResult> {
    const result = await firstValueFrom(
      this.http.post<PlanChangeResult>(`admin/companies/${companyId}/change-plan`, { priceId })
    );
    this.dataChangedSubject.next(true);
    return result;
  }

  async updateAccountStatus(companyId: string, accountStatus: AccountStatus) {
    const result = await this.http
      .patch<{ message: string; company: Company }>(`admin/companies/${companyId}/account-status`, { accountStatus })
      .toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  // CRAT-1220 — fix up the details that feed the shipping-invoice PDF customer block.
  // The API validates address as a nested DTO: send either no address key at all, or the
  // complete address object — a partial address object is rejected with a 422.
  async updateInvoiceDetails(companyId: string, data: { vat?: string; address?: GeoAddress }) {
    const result = await this.http
      .put<{ message: string; company: Company }>(`admin/companies/${companyId}`, data)
      .toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }

  async createCompany(data: {
    companyName: string;
    countryCode: string;
    phone?: string;
    vat?: string;
    fullName: string;
    email: string;
    password: string;
    /** CRAT-1373 — manual trial length in days (1–365); omitted = no trial dates. */
    trialDays?: number;
    /** CRAT-1373 — starting tier id; omitted = no tier. */
    companyTier?: string;
  }) {
    const result = await this.http
      .post<{ message: string; company: Company; user: any }>('admin/companies/create', data)
      .toPromise();
    this.dataChangedSubject.next(true);
    return result;
  }
}
